/**
 * /campaigns/:id/brief — submit a brief inside the campaign.
 *
 * Brief is now lightweight: platform, tone, duration. Brand context
 * comes from the campaign (product, audience, voice, model, colors)
 * so admins don't re-enter it each iteration. Each submit creates a
 * new marketing_video_jobs row; on success we navigate to the job's
 * variants view (/campaigns/:id/jobs/:jobId).
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiJson } from '../../lib/api';
import type { Character, Platform, Tone } from '../../lib/types';
import { useCampaignContext } from '../CampaignDetail';

const PLATFORMS: { value: Platform; label: string }[] = [
  { value: 'reels', label: 'Instagram Reels' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'shorts', label: 'YouTube Shorts' },
];

const TONES: { value: Tone; label: string }[] = [
  { value: 'punchy', label: 'Punchy' },
  { value: 'aspirational', label: 'Aspirational' },
  { value: 'practical', label: 'Practical' },
  { value: 'founder-led', label: 'Founder-led' },
];

interface BriefResponse {
  job_id: string;
  campaign_id: string;
  variants: unknown[];
}

export function BriefTab() {
  const { campaign, refreshJobs } = useCampaignContext();
  const navigate = useNavigate();
  const [platform, setPlatform] = useState<Platform>('reels');
  const [tone, setTone] = useState<Tone>('punchy');
  const [duration, setDuration] = useState(20);
  const [characterId, setCharacterId] = useState('');
  const [characters, setCharacters] = useState<Character[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the character catalogue so the admin can optionally attach a
  // UGC persona to this brief. Failure is non-fatal — the picker just
  // stays empty and briefs generate without a character.
  useEffect(() => {
    let cancelled = false;
    apiJson<{ characters: Character[] }>('/api/marketing/characters?limit=200')
      .then((res) => {
        if (!cancelled) setCharacters(res.characters);
      })
      .catch(() => {
        if (!cancelled) setCharacters([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCharacter = characters?.find((c) => c.id === characterId) ?? null;

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiJson<BriefResponse>(
        `/api/marketing/campaigns/${campaign.id}/briefs`,
        {
          method: 'POST',
          body: JSON.stringify({
            platform,
            tone,
            duration_seconds: duration,
            character_id: characterId || undefined,
          }),
        },
      );
      await refreshJobs();
      navigate(`/campaigns/${campaign.id}/jobs/${res.job_id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate variants');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="brief-panel" style={{ maxWidth: 640 }}>
      <h2>New brief</h2>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginBottom: 16 }}>
        Brand context, voice, and model come from the campaign — submit a brief to generate three
        variants. Matched assets from the campaign library are attached automatically.
      </p>

      <div className="brief-grid">
        <label className="field">
          <span>Platform</span>
          <select value={platform} onChange={(e) => setPlatform(e.target.value as Platform)}>
            {PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Tone</span>
          <select value={tone} onChange={(e) => setTone(e.target.value as Tone)}>
            {TONES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="field">
        <span>Duration target</span>
        <input
          type="range"
          min={12}
          max={35}
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
        />
        <strong>{duration}s</strong>
      </label>

      <label className="field">
        <span>UGC character (optional)</span>
        <select value={characterId} onChange={(e) => setCharacterId(e.target.value)}>
          <option value="">No character — brand voice</option>
          {(characters ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.higgsfield_character_id ? ' · Higgsfield ✓' : ''}
            </option>
          ))}
        </select>
        <small style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
          {selectedCharacter
            ? `Script will be written in ${selectedCharacter.name}'s voice.`
            : characters && characters.length === 0
              ? (
                  <>
                    No characters yet — create one on the{' '}
                    <Link to="/characters" style={{ color: 'var(--color-primary)' }}>
                      Characters
                    </Link>{' '}
                    page.
                  </>
                )
              : 'Pick a persona to generate the script in their voice for a UGC reel.'}
        </small>
      </label>

      <details
        style={{
          background: 'var(--color-surface-alt)',
          borderRadius: 8,
          padding: '8px 12px',
          marginTop: 12,
        }}
      >
        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          Campaign context preview
        </summary>
        <dl style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>
          <Row label="Product" value={campaign.product} />
          <Row label="Audience" value={campaign.audience} />
          <Row label="Offer" value={campaign.offer} />
          <Row label="Proof" value={campaign.proof} />
          <Row label="Default CTA" value={campaign.default_cta} />
        </dl>
      </details>

      {error && (
        <div className="alert alert-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}

      <button
        type="button"
        className="btn btn-primary"
        style={{ width: '100%', marginTop: 16 }}
        disabled={submitting}
        onClick={submit}
      >
        {submitting ? 'Generating…' : 'Generate 3 variants'}
      </button>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '2px 0' }}>
      <dt style={{ minWidth: 80, fontWeight: 600 }}>{label}</dt>
      <dd style={{ margin: 0, flex: 1 }}>{value}</dd>
    </div>
  );
}
